import { useState, useEffect, useCallback } from 'react';
import Header from '../../header';
import { useCurrentOrganization } from 'src/contexts/OrganizationContext';
import { apiPost, apiGet } from 'src/utils/api';
import { MatchPatientBody } from 'src/shared/types';
import Input from '../inputs/Input';
import SelectInput from '../inputs/SelectInput';
import { MagnifyingGlassIcon, ExclamationCircleIcon, ChevronDownIcon, UserPlusIcon } from '@heroicons/react/24/solid';
import { PatientLookupResult } from '../modals/PatientLookupModal';

type Tab = 'search' | 'create';

const PatientDashboard = () => {
  const organization = useCurrentOrganization();
  const [tab, setTab] = useState<Tab>('search');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [legalSex, setLegalSex] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [ssn, setSsn] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [results, setResults] = useState<PatientLookupResult[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults([]);
    setSelectedId(null);

    if (!organization) {
      setError('Organization not found');
      return;
    }

    setLoading(true);
    try {
      const matchBody: MatchPatientBody = {
        firstName,
        lastName,
        birthDate,
        phone,
      };
      if (legalSex) matchBody.gender = legalSex;
      if (email) matchBody.email = email;

      const response = await apiPost(
        `/api/${organization.id}/ehr/match-patient`,
        matchBody
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Match failed');
      }

      const data = await response.json();

      if (data.tempPatientId) {
        setResults([]);
        setSelectedId(null);
        return;
      }

      const patientId = data.patientId;
      const patients: PatientLookupResult[] = patientId ? [{
        patientId: data.patientId,
        displayName: data.displayName,
        birthDate: data.birthDate,
      }] : [];
      setResults(patients);
      if (patientId) setSelectedId(patientId);
      else setSelectedId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while searching.');
    } finally {
      setLoading(false);
      setHasSearched(true);
    }
  };

  const handleSelectPatient = useCallback(() => {
    if (selectedId && organization) {
      window.location.href = `/${organization.id}/dashboard/${selectedId}`;
    }
  }, [ selectedId, organization ]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization) return;
    setError('');
    if (!ssn) {
      setError('SSN is required to create a patient.');
      return;
    }
    setCreateLoading(true);
    try {
      const body: MatchPatientBody = {
        firstName,
        lastName,
        birthDate,
        phone,
        ssn,
        gender: legalSex,
      };
      if (email) body.email = email;

      const response = await apiPost(`/api/${organization.id}/ehr/create-patient`, body);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create patient');
      }
      const data = await response.json();
      if (data.patientId) {
        window.location.href = `/${organization.id}/dashboard/${data.patientId}`;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while creating the patient.');
    } finally {
      setCreateLoading(false);
    }
  };

  const switchTab = (next: Tab) => {
    setError('');
    setTab(next);
  };

  useEffect(() => {
    if (!organization) return;
    const params = new URLSearchParams(window.location.search);
    const autofill = params.get('autofill');
    if (!autofill) return;

    apiGet(`/api/${organization.id}/ehr/temp-patient/${autofill}`)
      .then(r => r.json())
      .then(data => {
        if (data.firstName) setFirstName(data.firstName);
        if (data.lastName) setLastName(data.lastName);
        if (data.birthDate) setBirthDate(data.birthDate);
        if (data.gender) setLegalSex(data.gender);
        if (data.ssn) setSsn(data.ssn);
        if (data.phone) setPhone(data.phone);
        if (data.email) setEmail(data.email);
        setTab('create');
      })
      .catch(() => {});
  }, [organization]);

  useEffect(() => {
    if (!organization) return;

    const source = new EventSource(`/api/${organization.id}/screen-pop/stream`);

    source.onmessage = (e: MessageEvent) => {
      const { patientId } = JSON.parse(e.data) as { patientId: string };
      if (patientId) {
        setSelectedId(patientId);
        handleSelectPatient();
      }
    };

    return () => source.close();
  }, [ organization, handleSelectPatient ]);

  const tabButtonClass = (active: boolean) =>
    `flex-1 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
      active
        ? 'border-brand-600 text-brand-600'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`;

  return (
    <div className="min-h-screen bg-gray-100">
      <Header showLogo={true} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <button
            type="button"
            onClick={() => setFormOpen((o) => !o)}
            className="w-full flex items-center justify-between px-6 py-4 text-left"
          >
            <h2 className="text-lg font-semibold text-gray-900">Patient Lookup</h2>
            <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${formOpen ? 'rotate-180' : ''}`} />
          </button>
          {formOpen && (
            <div className="px-6 pb-6">
              <div className="flex border-b border-gray-200 mb-6">
                <button
                  type="button"
                  onClick={() => switchTab('search')}
                  className={tabButtonClass(tab === 'search')}
                >
                  Find Patient
                </button>
                <button
                  type="button"
                  onClick={() => switchTab('create')}
                  className={tabButtonClass(tab === 'create')}
                >
                  Create Patient
                </button>
              </div>

              <form onSubmit={tab === 'search' ? handleSearch : handleCreate} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    type="text"
                    label="First Name"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                  <Input
                    type="text"
                    label="Last Name"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4`}>
                  <Input
                    type="date"
                    label="Date of Birth"
                    placeholder="YYYY-MM-DD"
                    value={birthDate}
                    onChange={(e) => setBirthDate(e.target.value)}
                    required
                  />
                  <Input
                    type="tel"
                    label="Phone"
                    placeholder="123-456-7890"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required={true}
                  />
                </div>
                <div className={`grid grid-cols-1 ${tab === 'create' ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-4`}>
                  <SelectInput
                    label="Legal Sex"
                    placeholder="Select legal sex"
                    value={legalSex}
                    onChange={(e) => setLegalSex(e.target.value)}
                    options={[
                      { value: 'male', label: 'Male' },
                      { value: 'female', label: 'Female' },
                    ]}
                    required={tab === 'create'}
                  />
                  <Input
                    type="email"
                    label="Email"
                    placeholder="patient@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required={false}
                  />
                  {tab === 'create' && (
                    <Input
                      type="text"
                      label="SSN"
                      placeholder="000-00-0000"
                      value={ssn}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 9);
                        if (val.length === 9) {
                          setSsn(`${val.slice(0, 3)}-${val.slice(3, 5)}-${val.slice(5)}`);
                        } else {
                          setSsn(val);
                        }
                      }}
                      required={true}
                    />
                  )}
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <div className="flex justify-end">
                  {tab === 'search' ? (
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex items-center justify-center gap-2 w-full sm:w-64 px-8 py-3 rounded-lg text-base font-semibold bg-brand-600 hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white transition-colors"
                    >
                      <MagnifyingGlassIcon className="h-5 w-5" />
                      {loading ? 'Searching...' : 'Search'}
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={createLoading}
                      className="flex items-center justify-center gap-2 w-full sm:w-64 px-8 py-3 rounded-lg text-base font-semibold bg-brand-600 hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white transition-colors"
                    >
                      <UserPlusIcon className="h-5 w-5" />
                      {createLoading ? 'Creating...' : 'Create Patient'}
                    </button>
                  )}
                </div>
              </form>

              {tab === 'search' && results.length > 0 && (
                <div className="-mx-6 -mb-6 mt-8 px-6 py-6 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Results</h3>
                  <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-white">
                        <tr>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DOB</th>
                          <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient ID</th>
                          <th scope="col" className="px-4 py-2 w-12" />
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {results.map((row) => (
                          <tr
                            key={row.patientId}
                            onClick={() => setSelectedId(row.patientId)}
                            className={`cursor-pointer hover:bg-gray-50 ${selectedId === row.patientId ? 'bg-white' : ''}`}
                          >
                            <td className="px-4 py-3 text-sm text-gray-900">{row.displayName ?? '—'}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{row.birthDate ?? '—'}</td>
                            <td className="px-4 py-3 text-sm font-mono text-gray-600">{row.patientId}</td>
                            <td className="px-4 py-3">
                              <input
                                type="radio"
                                name="patient-select"
                                checked={selectedId === row.patientId}
                                onChange={() => setSelectedId(row.patientId)}
                                className="h-4 w-4 text-brand-600 border-gray-300 focus:ring-brand-500"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={handleSelectPatient}
                      disabled={!selectedId}
                      className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-600 hover:bg-brand-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white transition-colors"
                    >
                      Select patient
                    </button>
                  </div>
                </div>
              )}

              {tab === 'search' && !loading && results.length === 0 && hasSearched && (
                <div className="-mx-6 -mb-6 mt-8 px-6 py-6 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                  <p className="text-sm text-gray-500 mb-3">No matching patients found. Create one using the details you entered.</p>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => switchTab('create')}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-brand-600 hover:bg-brand-700 text-white transition-colors"
                    >
                      <UserPlusIcon className="h-4 w-4" />
                      Create Patient
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="py-12 text-center">
          <img
            src="/Awaiting_patient_Calls_Icon.svg"
            alt="Awaiting patient calls"
            className="mx-auto mb-4 w-48 h-48 animate-pulse"
          />
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Awaiting Patient Interaction</h1>
          <p className="text-sm text-gray-500 mt-4">
            Your dashboard is ready. Patient details, medical history, and call information will appear here once a call is received.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PatientDashboard;
